import React, { useMemo, useState } from 'react';
import BaseTabs from 'components/atoms/base-tabs';
import { useTranslation } from 'react-i18next';
import PatientHoldingStatusLog from 'components/molecules/admin-panel/forms/patient-holding-status-log';
import PatientBlock from 'components/molecules/patientBlock';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import PatientCcmClaimStatusLog from 'components/molecules/admin-panel/forms/patient-ccm-claim-status-log';
import PatientRpmClaimStatusLog from 'components/molecules/admin-panel/forms/patient-rpm-claim-status-log';
import { makeid } from 'utils/helpers/makeid';
import PatientConsumableLog from 'components/molecules/admin-panel/forms/consumable-log';
import PatientEditorLog from 'components/molecules/admin-panel/forms/patient-editor-log';
import PatientThresholdLog from 'components/molecules/admin-panel/forms/threshold-log';
import PatientDeletedNotesLog from 'components/molecules/admin-panel/forms/notes-log';
import {
  isAdminModule,
  isCcmModule,
  isCombinedModule,
  isRpmModule,
} from 'infrastructure/functions';
import useUser from 'utils/useUser';
import { PlatformEnums, type ModuleType } from 'infrastructure/enums';
import PatientInteractiveStatusLog from 'components/molecules/admin-panel/forms/patient-interactive-status-log';
import RpmCcmStatusLog from 'components/molecules/admin-panel/forms/combined-status-log';
import ChartSessionLog from 'components/molecules/admin-panel/forms/chart-session-log';

import s from './styles.module.scss';

import type { DateRange } from 'infrastructure/types';
import type { IBaseTab } from 'components/atoms/base-tabs';

interface IPatientLogTabsProps {
  patientGuid: string;
  module: ModuleType;
}

const PatientLogTabs: React.FC<IPatientLogTabsProps> = (props) => {
  const { patientGuid, module } = props;

  const { isAdmin } = useUser();

  const [key, setKey] = useState<string>(makeid(5));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const { t } = useTranslation();

  const tabs: IBaseTab[] = useMemo(() => {
    const items = [
      {
        key: `rpmCcmStatusLog-${key}`,
        label: t('labels.rpmCcmStatusLog'),
        children: (
          <RpmCcmStatusLog patientGuid={patientGuid} dateRange={dateRange} />
        ),
      },
      {
        key: `rpmInteractionStatusLog-${key}`,
        label: t('labels.rpmInteractionStatusLog'),
        children: (
          <PatientInteractiveStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
            module={PlatformEnums.RPM}
          />
        ),
      },
      {
        key: `ccmInteractionStatusLog-${key}`,
        label: t('labels.ccmInteractionStatusLog'),
        children: (
          <PatientInteractiveStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
            module={PlatformEnums.CCM}
          />
        ),
      },
      {
        key: `rpmClaimStatusLog-${key}`,
        label: t('labels.rpmClaimStatusLog'),
        children: (
          <PatientRpmClaimStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `ccmClaimStatusLog-${key}`,
        label: t('labels.ccmClaimStatusLog'),
        children: (
          <PatientCcmClaimStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `holdingStatusLog-${key}`,
        label: t('labels.holdingStatusLog'),
        children: (
          <PatientHoldingStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `chartSessionLog-${key}`,
        label: t('labels.viewLog'),
        children: (
          <ChartSessionLog patientGuid={patientGuid} dateRange={dateRange} />
        ),
      },
    ];

    if (isAdminModule(module)) {
      items.push({
        key: `historyLog-${key}`,
        label: t('labels.historyLog'),
        children: (
          <PatientEditorLog patientGuid={patientGuid} dateRange={dateRange} />
        ),
      });
    }
    if (isCombinedModule(module) || isRpmModule(module)) {
      items.push({
        key: `consumablesLog-${key}`,
        label: t('labels.consumablesLog'),
        children: (
          <PatientConsumableLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }
    if (!isCcmModule(module)) {
      items.push({
        key: `thresholdLog-${key}`,
        label: t('labels.thresholdsLog'),
        children: (
          <PatientThresholdLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }

    if (isAdmin) {
      items.push({
        key: `noteActionLog-${key}`,
        label: t('labels.noteActionLog'),
        children: (
          <PatientDeletedNotesLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }
    return items;
  }, [dateRange]);

  return (
    <PatientBlock
      title={t('labels.patientLogs')}
      canDelete={false}
      canEdit={false}
    >
      <div className={s.wrapper}>
        <BaseTabs
          key={key}
          tabs={tabs}
          defaultIndex={selectedIndex}
          contentClassName={s.content}
          type="rounded"
          onChange={(_, index) => setSelectedIndex(index)}
          wrapperClassName={s['tabs-wrapper']}
        />
        <div className={s['date-picker']}>
          <BaseRangeDatepicker
            withPresets
            onChange={(range) => {
              setKey(makeid(5));
              setDateRange(range);
            }}
          />
        </div>
      </div>
    </PatientBlock>
  );
};

export default PatientLogTabs;
